import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const Roofing = () => {
  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my roofing business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps roofing businesses capture emergency leak calls 24/7, send instant quotes, reduce no-shows, and secure both residential and commercial contracts. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for roofing companies?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most roofing businesses see ROI within the first month through faster quote response and reduced no-shows."
        }
      },
      {
        "@type": "Question",
        "name": "Can I capture emergency calls 24/7?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes includes 24/7 emergency call capture so you never miss a leak or storm damage call, even when you're on a roof."
        }
      },
      {
        "@type": "Question",
        "name": "Will it help me win more commercial contracts?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Features include instant quote generation, professional proposals, and automated follow-up sequences that help you compete for and win larger commercial roofing contracts."
        }
      }
    ]
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Roofing - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Roofing CRM", url: "/industries/roofing" }
  ];

  const industryData = {
    name: 'Roofing',
    icon: '🏠',
    title: 'Stop Losing High-Value Roofing Contracts to Faster Competitors',
    subtitle: 'Capture emergency leak calls 24/7, send instant quotes, and secure both residential & commercial roofing projects on autopilot',
    
    // Problem Section - Challenges they face
    challenges: [
      'Missing emergency leak calls because you\'re on a roof and can\'t answer the phone',
      'Quote requests take days to respond to - customers book competitors who reply faster',
      'One-off residential jobs only - no pipeline of recurring commercial contracts',
      'Spending entire evenings measuring roofs and preparing quotes manually',
      'Customers ghost you after receiving your quote with no explanation',
      'Weather delays projects but customers don\'t get timely updates',
      'Completed beautiful work but customers never leave Google reviews',
      'Warranty claims and annual inspections slip through the cracks'
    ],
    
    // Solution Section - How we solve these
    solutions: [
      'Emergency leak request form captures urgent calls 24/7 with automatic SMS acknowledgment so customers know you\'ve got their emergency covered',
      'Photo upload survey system lets customers share roof images and property details before you even visit - qualify leads remotely',
      'Instant quote calculator provides ballpark pricing for re-roofing projects based on square footage and roof type (tiles, slate, flat) before site visits',
      'Commercial client CRM tracks relationships with property managers, housing associations, and developers for recurring maintenance contracts',
      'Automated project updates via SMS at every stage: survey booked → quote sent → materials ordered → work started → project completed',
      'Weather delay notifications automatically inform customers about rain delays and rescheduling',
      'Review automation requests 5-star Google reviews after project completion with before/after photo showcase'
    ],
    
    // Features Section - What's included in snapshot
    features: [
      '24/7 Emergency Leak Request Form with Priority Routing',
      'Photo Upload Roof Survey System (customers share images remotely)',
      'Instant Re-Roofing Quote Calculator (by sq ft & roof type)',
      'Emergency Response Missed Call Auto-Text System',
      'Commercial Client CRM & Pipeline Tracker',
      'Automated Project Stage Updates (SMS & Email)',
      'Weather Delay Notification Automation',
      'Before/After Project Photo Gallery',
      'Warranty Registration & Annual Inspection Reminders',
      'Google Review Request Automation',
      'Coverage Area Mapper (define service postcodes)',
      'Subcontractor Coordination Scheduler',
      'Material Order Tracking & Customer Notifications',
      'Insurance Claims Documentation Portal'
    ],
    
    // Results Section - Success metrics
    results: [
      '70% quote conversion rate increase',
      '24/7 emergency call capture',
      '3x more commercial contracts secured'
    ],
    
    // SEO Section
    metaTitle: 'Roofing Business Growth Platform | Win More High-Value Roofing Contracts',
    metaDescription: 'Stop missing emergency leak calls & win more roofing projects. Automated quote system, 24/7 lead capture, and commercial client CRM for roofers.',
    
    // Reviews Section
    reviews: [
      {
        author: 'Michael T.',
        business: 'Residential & Commercial Roofer',
        location: 'Yorkshire',
        rating: 5,
        text: 'We were losing so many emergency leak calls because we\'re up on roofs all day. Now every emergency inquiry gets captured automatically with an instant text response. Our emergency callout work has doubled, and we\'re booking 3-4 full re-roofing jobs every week from the quote calculator on our site. Game changer.',
        verified: true
      },
      {
        author: 'Sarah L.',
        business: 'Flat Roofing Specialist',
        location: 'London',
        rating: 5,
        text: 'The photo upload survey system is brilliant. Customers send roof photos and property details before I even visit, so I can qualify leads and provide rough quotes remotely. Saves me hours of driving to properties that aren\'t a good fit. Quote conversion went from 30% to 68% in 3 months.',
        verified: true
      },
      {
        author: 'James R.',
        business: 'Commercial Roofing Contractor',
        location: 'Manchester',
        rating: 5,
        text: 'The commercial CRM is exactly what we needed. We now track all our relationships with property managers and housing associations properly. Set up automated annual inspection reminders for all our warranty clients. Secured 4 new maintenance contracts worth £80K+ in the first 6 months.',
        verified: true
      },
      {
        author: 'David K.',
        business: 'Roofing & Guttering Services',
        location: 'Birmingham',
        rating: 5,
        text: 'Customers love the automated project updates. They get SMS notifications when materials are ordered, when we\'re starting, and when the job\'s complete. The weather delay notifications are great too - no more angry calls about rain delays. Our Google reviews have gone from 8 to 47 in 4 months.',
        verified: true
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Roofers UK | Roofing Business Software | Local Business Heroes"
        description="Rated 4.9/5. CRM for roofing businesses. 24/7 emergency call capture, instant quotes, job scheduling. Grow your roofing business. Start £5 trial today."
        keywords="CRM for roofers UK, roofing business software, roofer lead management, roofing quote software UK, roof repair software, commercial roofing CRM"
        url="/industries/roofing"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default Roofing;